import { c as _c } from "react/compiler-runtime";
import { IconArchive, IconBell, IconBellCog, IconChecks, IconInbox } from "@tabler/icons-react";
import Link from "next/link";
import { useEffect, useRef, useState, useTransition } from "react";
import { NotificationList } from "@/components/notifications/notification-list";
import { useNotificationsData } from "@/hooks/use-notifications-data";
import type { NotificationData } from "@domainstack/types";
import { Button } from "@domainstack/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@domainstack/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@domainstack/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
export function NotificationsPopover() {
  const $ = _c(83);
  const loadMoreRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const [view, setView] = useState("inbox");
  const [open, setOpen] = useState(false);
  const [, startTransition] = useTransition();
  const autoMarkedThisOpenRef = useRef(false);
  const skipAutoMarkOnCloseRef = useRef(false);
  const filter = view === "inbox" ? "unread" : "read";
  let t0;
  if ($[0] !== filter || $[1] !== open) {
    t0 = {
      filter,
      enabled: open
    };
    $[0] = filter;
    $[1] = open;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    notifications,
    count,
    showLoading,
    hasNextPage,
    isFetchingNextPage,
    isError: isNotificationsError,
    markAllRead,
    markRead,
    fetchNextPage,
    getLatestUnreadCount
  } = useNotificationsData(t0);
  let t1;
  let t2;
  if ($[3] !== open) {
    t1 = () => {
      if (open) {
        autoMarkedThisOpenRef.current = false;
        skipAutoMarkOnCloseRef.current = false;
      }
    };
    t2 = [open];
    $[3] = open;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== getLatestUnreadCount || $[7] !== markAllRead || $[8] !== view) {
    t3 = () => {
      if (autoMarkedThisOpenRef.current) {
        return;
      }
      if (markAllRead.isPending) {
        return;
      }
      if (view !== "inbox") {
        return;
      }
      const latestUnreadCount = getLatestUnreadCount();
      if (latestUnreadCount <= 0) {
        return;
      }
      autoMarkedThisOpenRef.current = true;
      markAllRead.mutate(undefined, {
        onError: () => {
          autoMarkedThisOpenRef.current = false;
        }
      });
    };
    $[6] = getLatestUnreadCount;
    $[7] = markAllRead;
    $[8] = view;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const maybeAutoMarkAllRead = t3;
  let t4;
  if ($[10] !== maybeAutoMarkAllRead) {
    t4 = () => {
      maybeAutoMarkAllRead();
      setOpen(false);
    };
    $[10] = maybeAutoMarkAllRead;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const closePopover = t4;
  let t5;
  if ($[12] !== markRead) {
    t5 = notification => {
      if (!notification.readAt) {
        markRead.mutate({
          id: notification.id
        });
      }
      skipAutoMarkOnCloseRef.current = true;
      setOpen(false);
    };
    $[12] = markRead;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleNotificationClick = t5;
  let t6;
  let t7;
  if ($[14] !== fetchNextPage || $[15] !== hasNextPage || $[16] !== isFetchingNextPage || $[17] !== open) {
    t6 = () => {
      const scrollContainer = scrollAreaRef.current;
      const loadMoreElement = loadMoreRef.current;
      if (!open || !loadMoreElement || !hasNextPage || isFetchingNextPage) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      }, {
        threshold: 0.1,
        root: scrollContainer
      });
      observer.observe(loadMoreElement);
      return () => observer.disconnect();
    };
    t7 = [fetchNextPage, hasNextPage, isFetchingNextPage, open];
    $[14] = fetchNextPage;
    $[15] = hasNextPage;
    $[16] = isFetchingNextPage;
    $[17] = open;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  useEffect(t6, t7);
  let t8;
  if ($[20] !== maybeAutoMarkAllRead) {
    t8 = nextOpen => {
      if (!nextOpen) {
        if (skipAutoMarkOnCloseRef.current) {
          skipAutoMarkOnCloseRef.current = false;
        } else {
          maybeAutoMarkAllRead();
        }
      }
      setOpen(nextOpen);
    };
    $[20] = maybeAutoMarkAllRead;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = `Notifications${count > 0 ? ` (${count})` : ""}`;
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IconBell />;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== count) {
    t11 = count > 0 && <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-destructive ring-2 ring-background" aria-hidden={true} />;
    $[23] = count;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t9) {
    t12 = <TooltipTrigger render={<PopoverTrigger render={<Button variant="ghost" size="icon-sm" className="relative" aria-label={t9}>{t10}{t11}</Button>} />} />;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = count > 0 ? `Notifications (${count})` : "Notifications";
  let t14;
  if ($[28] !== t13) {
    t14 = <TooltipContent>{t13}</TooltipContent>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t14) {
    t15 = <Tooltip>{t12}{t14}</Tooltip>;
    $[30] = t12;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      side: "none",
      align: "shift"
    };
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <h4 className="text-[15px] font-semibold">Notifications</h4>;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <IconBellCog className="size-3.5 shrink-0 text-foreground/90" />;
    t19 = <span className="sr-only">Settings</span>;
    $[35] = t18;
    $[36] = t19;
  } else {
    t18 = $[35];
    t19 = $[36];
  }
  let t20;
  if ($[37] !== closePopover) {
    t20 = <TooltipTrigger render={<Button variant="ghost" size="icon-sm" aria-label="Notification settings" nativeButton={false} render={<Link href="/settings/notifications" onNavigate={closePopover}>{t18}{t19}</Link>} />} />;
    $[37] = closePopover;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <TooltipContent>Settings</TooltipContent>;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t20) {
    t22 = <div className="flex items-center justify-between pt-2 pr-4 pb-1 pl-4">{t17}<Tooltip>{t20}{t21}</Tooltip></div>;
    $[40] = t20;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== maybeAutoMarkAllRead || $[43] !== startTransition || $[44] !== view) {
    t23 = v => {
      const nextView = v as typeof view;
      if (view === "inbox" && nextView === "archive") {
        maybeAutoMarkAllRead();
      }
      startTransition(() => setView(nextView));
      if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollTop = 0;
      }
    };
    $[42] = maybeAutoMarkAllRead;
    $[43] = startTransition;
    $[44] = view;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <IconInbox className="!text-inherit" aria-hidden={true} />;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== count) {
    t25 = count > 0 && <span className="ml-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-foreground px-1.5 text-[10px] font-medium text-background tabular-nums">{count > 99 ? "99+" : count}</span>;
    $[47] = count;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t25) {
    t26 = <TabsTrigger value="inbox" className="flex-initial gap-2 text-[13px] transition-colors hover:text-foreground">{t24}Inbox{t25}</TabsTrigger>;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <TabsTrigger value="archive" className="flex-initial gap-2 text-[13px] transition-colors hover:text-foreground"><IconArchive className="!text-inherit" aria-hidden={true} />Archive</TabsTrigger>;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== count || $[53] !== markAllRead || $[54] !== view) {
    t28 = view === "inbox" && count > 0 && <div className="ml-auto flex items-center"><Button variant="ghost" size="sm" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending} className="h-full text-[13px] text-muted-foreground" aria-label="Clear all notifications"><IconChecks className="size-4" aria-hidden={true} /><span className="max-sm:sr-only">Clear all</span></Button></div>;
    $[52] = count;
    $[53] = markAllRead;
    $[54] = view;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t26 || $[57] !== t28) {
    t29 = <TabsList variant="line">{t26}{t27}{t28}</TabsList>;
    $[56] = t26;
    $[57] = t28;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== t23 || $[60] !== t29 || $[61] !== view) {
    t30 = <div className="pl-1.5"><Tabs value={view} onValueChange={t23}>{t29}</Tabs></div>;
    $[59] = t23;
    $[60] = t29;
    $[61] = view;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== t22 || $[64] !== t30) {
    t31 = <div className="shrink-0 border-b border-border bg-card/60">{t22}{t30}</div>;
    $[63] = t22;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== closePopover || $[67] !== handleNotificationClick || $[68] !== hasNextPage || $[69] !== isFetchingNextPage || $[70] !== isNotificationsError || $[71] !== notifications || $[72] !== showLoading || $[73] !== view) {
    t32 = <NotificationList notifications={notifications} isLoading={showLoading} isError={isNotificationsError} view={view} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} loadMoreRef={loadMoreRef} scrollAreaRef={scrollAreaRef} onNotificationClick={handleNotificationClick} onClosePopover={closePopover} />;
    $[66] = closePopover;
    $[67] = handleNotificationClick;
    $[68] = hasNextPage;
    $[69] = isFetchingNextPage;
    $[70] = isNotificationsError;
    $[71] = notifications;
    $[72] = showLoading;
    $[73] = view;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== t31 || $[76] !== t32) {
    t33 = <PopoverContent className="overflow-hidden bg-background p-0 max-sm:!right-0 max-sm:!left-0 max-sm:!mx-auto max-sm:w-[calc(100vw-1rem)] max-sm:!translate-x-0 sm:w-[400px]" align="end" side="bottom" collisionAvoidance={t16} collisionPadding={8}><div className="flex max-h-[calc(min(100dvh-6rem,560px))] flex-col">{t31}{t32}</div></PopoverContent>;
    $[75] = t31;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== open || $[79] !== t15 || $[80] !== t33 || $[81] !== t8) {
    t34 = <Popover open={open} onOpenChange={t8}>{t15}{t33}</Popover>;
    $[78] = open;
    $[79] = t15;
    $[80] = t33;
    $[81] = t8;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  return t34;
}